import React, { useMemo, useState, useEffect } from "react";
import { useGlobalContext } from "../Config";

import { Dropdown } from "primereact/dropdown";
import { Button } from "primereact/button";
import { TabView, TabPanel } from "primereact/tabview";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { ProgressSpinner } from "primereact/progressspinner";

// OJO: en tu EstadoMedicion original importabas formatDateCL, pero finalmente mostrabas lecturaFecha "tal cual".
// Si quieres volver a formatear, descomenta la importación y usa lecturaFechaBody.
// import { formatDateCL } from "../utilities/DateUtil";

const RevisionPeriodo = () => {
  const { baseUrl } = useGlobalContext();
  const { urlDocumentos } = useGlobalContext();

  // loading global (buscar + generar boletas)
  const [isLoading, setIsLoading] = useState(false);

  // filtros unificados
  const [empresaId, setEmpresaId] = useState(null);
  const [mes, setMes] = useState(null); // 1..12
  const [anio, setAnio] = useState(null); // 2024..2026
  const [tipoId, setTipoId] = useState(null); // opcional (Estado)

  // datos Estado
  const [estadoResp, setEstadoResp] = useState(null);
  const [activeEstadoTab, setActiveEstadoTab] = useState(0);

  // datos Detalle
  const [detalleItems, setDetalleItems] = useState([]);
  const [boletasInfo, setBoletasInfo] = useState([]);
  const [lastQuery, setLastQuery] = useState(null);

  // UI tabs principales
  const [activeMainTab, setActiveMainTab] = useState(0); // 0=Estado, 1=Detalle

  // tipos (dropdown)
  const [tipos, setTipos] = useState([]);

  const empresasOptions = [
    { label: "ALTAMIRA", value: 1 },
    { label: "LA SIEMBRA", value: 2 },
  ];

  const mesesOptions = [
    { label: "Enero", value: 1 },
    { label: "Febrero", value: 2 },
    { label: "Marzo", value: 3 },
    { label: "Abril", value: 4 },
    { label: "Mayo", value: 5 },
    { label: "Junio", value: 6 },
    { label: "Julio", value: 7 },
    { label: "Agosto", value: 8 },
    { label: "Septiembre", value: 9 },
    { label: "Octubre", value: 10 },
    { label: "Noviembre", value: 11 },
    { label: "Diciembre", value: 12 },
  ];

  const aniosOptions = [2024, 2025, 2026].map((y) => ({
    label: String(y),
    value: y,
  }));

  const buildPeriodo = () => {
    if (!anio || !mes) return "";
    return `${anio}-${String(mes).padStart(2, "0")}`;
  };

  const periodoLabel = useMemo(() => buildPeriodo() || "—", [anio, mes]);

  const fetchJson = async (url, options = {}) => {
    const res = await fetch(url, {
      credentials: "include",
      redirect: "follow",
      ...options,
      headers: {
        "Content-Type": "application/json",
        ...(options.headers || {}),
      },
    });

    const data = await res.json().catch(() => ({}));
    if (!res.ok || data?.respuesta === false) {
      const msg = data?.mensaje || `Error HTTP ${res.status}`;
      throw new Error(msg);
    }
    return data;
  };

  const validar = () => {
    if (!empresaId) {
      alert("Debe seleccionar empresa.");
      return false;
    }
    if (!anio || !mes) {
      alert("Debe seleccionar mes y año.");
      return false;
    }
    return true;
  };

  // --------- Estado Mediciones ---------
  useEffect(() => {
    (async () => {
      try {
        const data = await fetchJson(`${baseUrl}tipo/buscar`, {
          method: "GET",
        });
        setTipos(Array.isArray(data?.tipos) ? data.tipos : []);
      } catch (e) {
        setTipos([]);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tiposOptions = useMemo(() => {
    const base = [{ label: "Todos", value: null }];
    const dyn = (tipos || []).map((t) => ({
      label: t?.descripcion ?? `Tipo ${t?.id}`,
      value: t?.id ?? null,
    }));
    return base.concat(dyn);
  }, [tipos]);

  const buscarEstado = async (empId, y, m, tId) => {
    let url =
      `${baseUrl}estadoMediciones` +
      `?empresaId=${encodeURIComponent(empId)}` +
      `&anio=${encodeURIComponent(y)}` +
      `&mes=${encodeURIComponent(m)}`;

    if (tId != null) url += `&tipoId=${encodeURIComponent(tId)}`;
    const data = await fetchJson(url, { method: "GET" });
    return data;
  };

  const TIPO_LABEL = {
    1: "AGUA",
    2: "ELECTRICIDAD",
  };

  // const lecturaFechaBody = (row) => {
  //   const v = row?.lecturaFecha;
  //   return v ? formatDateCL(v) : "-";
  // };

  // --------- Detalle Periodo / Boletas ---------
  const PRESTADOR_RUT_BY_EMPRESA = {
    1: "76.071.007-5",
    2: "76.066.122-8",
  };

  const joinUrl = (base, path) =>
    `${String(base || "").replace(/\/+$/, "")}/${String(path || "").replace(
      /^\/+/,
      "",
    )}`;

  const keyBoleta = (remarcadorId, tipoServicio) =>
    `${String(remarcadorId ?? "")}||${String(
      tipoServicio ?? "",
    ).toUpperCase()}`;

  const mergeBoletasIntoItems = (baseItems, boletasRows) => {
    const map = new Map(
      (boletasRows || []).map((b) => [
        keyBoleta(b.remarcadorId, b.tipoServicio),
        b,
      ]),
    );

    return (baseItems || []).map((it) => ({
      ...it,
      boleta: map.get(keyBoleta(it.remarcadorId, it.tipoServicio)) || null,
    }));
  };

  const cargarBoletasGuardadas = async (empId, per) => {
    const urlBoletas =
      `${baseUrl}detallePeriodo/boletas` +
      `?empresaId=${encodeURIComponent(empId)}` +
      `&periodo=${encodeURIComponent(per)}`;

    const boletasData = await fetchJson(urlBoletas, { method: "GET" });
    const boletasRows = Array.isArray(boletasData?.items)
      ? boletasData.items
      : [];
    setBoletasInfo(boletasRows);
    return boletasRows;
  };

  const buscarDetalle = async (empId, per) => {
    const url =
      `${baseUrl}detallePeriodo` +
      `?empresaId=${encodeURIComponent(empId)}` +
      `&periodo=${encodeURIComponent(per)}`;

    const data = await fetchJson(url, { method: "GET" });
    const baseItems = Array.isArray(data?.items) ? data.items : [];

    let boletasRows = [];
    try {
      boletasRows = await cargarBoletasGuardadas(empId, per);
    } catch (e) {
      boletasRows = [];
      setBoletasInfo([]);
    }

    const merged = mergeBoletasIntoItems(baseItems, boletasRows);
    return merged;
  };

  const fmtMoneyCLP = (v) => {
    const n = Number(v ?? 0);
    if (Number.isNaN(n)) return "-";
    return n.toLocaleString("es-CL", { maximumFractionDigits: 0 });
  };

  const abrirPdfFacturador = (boleta) => {
    if (!boleta?.documentoId) {
      alert("No hay documentoId para esta boleta.");
      return;
    }
    const url = joinUrl(
      urlDocumentos,
      `descargar/boleta/pdf?idDocumento=${encodeURIComponent(
        boleta.documentoId,
      )}`,
    );
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const getPrevPeriodo = (p) => {
    const m = String(p || "").match(/^(\d{4})-(\d{2})$/);
    if (!m) return null;
    let y = Number(m[1]);
    let mm = Number(m[2]);
    mm -= 1;
    if (mm === 0) {
      mm = 12;
      y -= 1;
    }
    return `${y}-${String(mm).padStart(2, "0")}`;
  };

  const generarBoletas = async () => {
    const periodo = buildPeriodo();

    if (!empresaId || !periodo) {
      alert("Selecciona empresa y periodo.");
      return;
    }
    if (!detalleItems?.length) {
      alert("No hay items para generar boletas.");
      return;
    }

    setIsLoading(true);
    try {
      const rutEmpresa = PRESTADOR_RUT_BY_EMPRESA[empresaId];
      if (!rutEmpresa) throw new Error("Falta configurar RUT del prestador.");

      // 1) historial batch (medidores)
      const reqHist = {
        periodo,
        items: detalleItems.map((it) => ({
          codigoDomicilio: it.codigoDomicilio,
          tipoServicio: it.tipoServicio,
        })),
      };

      const endpointHistorialBatch = `${baseUrl}detallePeriodo/historial/batch`;
      const histData = await fetchJson(endpointHistorialBatch, {
        method: "POST",
        body: JSON.stringify(reqHist),
      });

      const histItems = Array.isArray(histData?.items) ? histData.items : [];

      const keyHist = (codigoDomicilio, tipoServicio) =>
        `${String(codigoDomicilio ?? "")}||${String(
          tipoServicio ?? "",
        ).toUpperCase()}`;

      const histMap = new Map(
        histItems.map((h) => [keyHist(h.codigoDomicilio, h.tipoServicio), h]),
      );

      // 2) construir boletas para Facturador
      const prev = getPrevPeriodo(periodo);

      const boletas = detalleItems.map((it) => {
        const h = histMap.get(keyHist(it.codigoDomicilio, it.tipoServicio));
        const historial = Array.isArray(h?.historialConsumo)
          ? [...h.historialConsumo].sort((a, b) =>
              String(a?.periodo || "").localeCompare(String(b?.periodo || "")),
            )
          : [];

        const consumoMesAnterior =
          historial.find((x) => x?.periodo === prev)?.consumo ?? 0;

        return {
          remarcadorId: it.remarcadorId,
          tipoServicio: it.tipoServicio,
          unidad: it.unidad,

          valorUnitario: it.valorUnitario,

          cliente: it.cliente,

          lecturaMesAnterior: it.lecturaMesAnterior ?? 0,
          lecturaMesActual: it.lecturaMesActual ?? 0,

          consumoMesAnterior: consumoMesAnterior ?? 0,
          consumoMesActual: it.consumoMesActual ?? 0,

          saldoAnterior: 0,

          historialConsumo: historial,
        };
      });

      const payloadFacturador = {
        periodo,
        prestador: { rutEmpresa },
        boletas,
      };

      const facturadorBatchEndpoint = joinUrl(
        urlDocumentos,
        "api/boletas/batch",
      );
      const resp = await fetchJson(facturadorBatchEndpoint, {
        method: "POST",
        body: JSON.stringify(payloadFacturador),
      });

      const factPayload = resp?.payload || null;
      const factItems = Array.isArray(factPayload?.items)
        ? factPayload.items
        : [];

      const guardarReq = {
        empresaId,
        periodo,
        items: factItems.map((x) => ({
          remarcadorId: x.remarcadorId,
          tipoServicio: x.tipoServicio,
          documentoId: x.documentoId ?? null,
          adjuntoPdfId: x.adjuntoPdfId ?? null,
          folio: x.folio ?? null,
          status: x.status ?? null,
          errores: Array.isArray(x.errores) ? x.errores : [],
        })),
      };

      await fetchJson(`${baseUrl}detallePeriodo/boletas/guardar-batch`, {
        method: "POST",
        body: JSON.stringify(guardarReq),
      });

      // 5) refrescar boletas guardadas y re-merge
      const boletasRows = await cargarBoletasGuardadas(empresaId, periodo);
      setDetalleItems((prevItems) =>
        mergeBoletasIntoItems(prevItems, boletasRows),
      );

      alert(
        factPayload?.mensajeGeneral ||
          resp?.mensaje ||
          "Batch procesado correctamente.",
      );
      // eslint-disable-next-line no-console
      console.log("Resp facturador:", resp);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      alert("Error generando boletas: " + (e?.message || e));
    } finally {
      setIsLoading(false);
    }
  };

  // --------- Buscar unificado ---------
  const buscar = async () => {
    if (!validar()) return;

    const per = buildPeriodo();
    const emp = empresaId;
    const y = anio;
    const m = mes;
    const tId = tipoId;

    setIsLoading(true);
    try {
      const [estadoRes, detalleRes] = await Promise.allSettled([
        buscarEstado(emp, y, m, tId),
        buscarDetalle(emp, per),
      ]);

      if (estadoRes.status === "fulfilled") {
        setEstadoResp(estadoRes.value);
        setActiveEstadoTab(0);
      } else {
        setEstadoResp(null);
        alert(
          "Error consultando estado mediciones: " +
            (estadoRes.reason?.message || estadoRes.reason),
        );
      }

      if (detalleRes.status === "fulfilled") {
        setDetalleItems(detalleRes.value);
        setLastQuery({ empresaId: emp, periodo: per });
      } else {
        setDetalleItems([]);
        setBoletasInfo([]);
        setLastQuery(null);
        alert(
          "Error consultando detalle periodo: " +
            (detalleRes.reason?.message || detalleRes.reason),
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  const limpiar = () => {
    setEmpresaId(null);
    setMes(null);
    setAnio(null);
    setTipoId(null);

    setEstadoResp(null);
    setActiveEstadoTab(0);

    setDetalleItems([]);
    setBoletasInfo([]);
    setLastQuery(null);

    setActiveMainTab(0);
  };

  // preselección mes/año actual
  useEffect(() => {
    const now = new Date();
    const y = now.getFullYear();
    const m = now.getMonth() + 1;
    if (y >= 2024 && y <= 2026) {
      setAnio(y);
      setMes(m);
    }
  }, []);

  // --------- UI helpers ---------
  const tablaProps = {
    responsiveLayout: "scroll",
    className: "p-datatable-sm",
    paginator: true,
    rows: 10,
    rowsPerPageOptions: [5, 10, 20],
    stripedRows: true,
    showGridlines: true,
  };

  const cardStyle = {
    padding: "14px 16px",
    backgroundColor: "#fff",
    borderRadius: "10px",
    boxShadow: "0px 0px 10px rgba(0, 0, 0, 0.05)",
  };

  const big = { fontSize: 22, fontWeight: 800, marginTop: 6 };

  const itemsLeidos = Array.isArray(estadoResp?.itemsLeidos)
    ? estadoResp.itemsLeidos
    : [];
  const itemsPend = Array.isArray(estadoResp?.itemsPendientes)
    ? estadoResp.itemsPendientes
    : [];

  const total = Number(
    estadoResp?.total ?? itemsLeidos.length + itemsPend.length,
  );
  const leidosCount = Number(estadoResp?.leidos ?? itemsLeidos.length);
  const pendientesCount = Number(estadoResp?.pendientes ?? itemsPend.length);
  const pctLeidos = total > 0 ? Math.round((leidosCount * 100) / total) : 0;

  return (
    <div className="col-12 md:col-12">
      {/* header */}
      <div
        style={{
          padding: "20px",
          backgroundColor: "#f8f9fa",
          borderRadius: "8px",
          boxShadow: "0px 0px 10px rgba(0, 0, 0, 0.1)",
          marginBottom: "20px",
        }}
      >
        <div>
          <h4 style={{ margin: 0, color: "#495057" }}>Revisión Período</h4>
          <small style={{ color: "#6c757d" }}>
            Estado de mediciones + detalle de consumo y boletas en una sola
            vista
          </small>
        </div>
      </div>

      {/* filtros unificados */}
      <div
        style={{
          padding: "20px",
          backgroundColor: "#fff",
          borderRadius: "8px",
          boxShadow: "0px 0px 10px rgba(0, 0, 0, 0.05)",
          marginBottom: "20px",
        }}
      >
        <div className="grid">
          <div className="col-12 md:col-3">
            <label style={{ fontWeight: "bold" }}>Empresa</label>
            <Dropdown
              value={empresaId}
              options={empresasOptions}
              onChange={(e) => setEmpresaId(e.value)}
              placeholder="Seleccione empresa"
              style={{ width: "100%" }}
              showClear
              disabled={isLoading}
            />
          </div>

          <div className="col-12 md:col-3">
            <label style={{ fontWeight: "bold" }}>Mes</label>
            <Dropdown
              value={mes}
              options={mesesOptions}
              onChange={(e) => setMes(e.value)}
              placeholder="Seleccione mes"
              style={{ width: "100%" }}
              showClear
              disabled={isLoading}
            />
          </div>

          <div className="col-12 md:col-2">
            <label style={{ fontWeight: "bold" }}>Año</label>
            <Dropdown
              value={anio}
              options={aniosOptions}
              onChange={(e) => setAnio(e.value)}
              placeholder="Año"
              style={{ width: "100%" }}
              showClear
              disabled={isLoading}
            />
          </div>

          <div className="col-12 md:col-2">
            <label style={{ fontWeight: "bold" }}>Tipo (opcional)</label>
            <Dropdown
              value={tipoId}
              options={tiposOptions}
              onChange={(e) => setTipoId(e.value)}
              placeholder="Todos"
              style={{ width: "100%" }}
              showClear={false}
              disabled={isLoading}
            />
          </div>

          <div className="col-12 md:col-2">
            <label style={{ fontWeight: "bold", opacity: 0 }}>Acciones</label>
            <div
              style={{
                display: "flex",
                gap: 10,
                alignItems: "center",
                height: "42px",
                marginTop: 4,
              }}
            >
              <Button
                label="Buscar"
                icon="pi pi-search"
                onClick={buscar}
                disabled={isLoading}
              />
              <Button
                label="Limpiar"
                icon="pi pi-times"
                className="p-button-text"
                onClick={limpiar}
                disabled={isLoading}
              />
            </div>
          </div>
        </div>
      </div>

      {isLoading && (
        <div style={{ textAlign: "center", margin: "20px 0" }}>
          <ProgressSpinner />
        </div>
      )}

      {/* tabs principales */}
      <div
        style={{
          padding: "20px",
          backgroundColor: "#fff",
          borderRadius: "8px",
          boxShadow: "0px 0px 10px rgba(0, 0, 0, 0.05)",
          marginBottom: "20px",
        }}
      >
        <TabView
          activeIndex={activeMainTab}
          onTabChange={(e) => setActiveMainTab(e.index)}
        >
          {/* TAB ESTADO */}
          <TabPanel header="Estado">
            {/* cards */}
            <div className="grid" style={{ marginBottom: 20 }}>
              <div className="col-12 md:col-3">
                <div style={cardStyle}>
                  <div style={{ color: "#6c757d", fontWeight: 700 }}>Total</div>
                  <div style={big}>{estadoResp?.total ?? 0}</div>
                  <div style={{ color: "#6c757d", fontSize: 12 }}>
                    Periodo: {estadoResp?.periodo || periodoLabel}
                  </div>
                </div>
              </div>

              <div className="col-12 md:col-3">
                <div style={cardStyle}>
                  <div style={{ color: "#6c757d", fontWeight: 700 }}>
                    Leídos
                  </div>
                  <div style={big}>{estadoResp?.leidos ?? 0}</div>
                  <div style={{ color: "#6c757d", fontSize: 12 }}>
                    Con lectura registrada en el periodo
                  </div>
                </div>
              </div>

              <div className="col-12 md:col-3">
                <div style={cardStyle}>
                  <div style={{ color: "#6c757d", fontWeight: 700 }}>
                    Pendientes
                  </div>
                  <div style={big}>{estadoResp?.pendientes ?? 0}</div>
                  <div style={{ color: "#6c757d", fontSize: 12 }}>
                    Sin lectura registrada en el periodo
                  </div>
                </div>
              </div>

              <div className="col-12 md:col-3">
                <div style={cardStyle}>
                  <div style={{ color: "#6c757d", fontWeight: 700 }}>
                    % Leídos
                  </div>
                  <div style={big}>{pctLeidos}%</div>
                  <div style={{ color: "#6c757d", fontSize: 12 }}>
                    {leidosCount} de {total} (pendientes: {pendientesCount})
                  </div>
                </div>
              </div>
            </div>

            {/* tablas estado */}
            <TabView
              activeIndex={activeEstadoTab}
              onTabChange={(e) => setActiveEstadoTab(e.index)}
            >
              <TabPanel header={`Leídos (${itemsLeidos.length})`}>
                <DataTable
                  value={itemsLeidos}
                  dataKey={(row) => String(row?.remarcadorId ?? "")}
                  {...tablaProps}
                >
                  <Column
                    field="remarcadorId"
                    header="Remarcador ID"
                    sortable
                  />
                  <Column field="qrCode" header="QR" sortable />
                  <Column
                    field="medidorPadreId"
                    header="Medidor padre"
                    sortable
                  />
                  <Column
                    header="Tipo"
                    body={(row) =>
                      TIPO_LABEL[row?.tipoId] ?? String(row?.tipoId ?? "-")
                    }
                    sortable
                    sortField="tipoId"
                  />
                  <Column
                    header="Fecha lectura"
                    body={(row) => row?.lecturaFecha ?? "-"}
                    sortable
                    sortField="lecturaFecha"
                  />
                </DataTable>
              </TabPanel>

              <TabPanel header={`Pendientes (${itemsPend.length})`}>
                <DataTable
                  value={itemsPend}
                  dataKey={(row) => String(row?.remarcadorId ?? "")}
                  {...tablaProps}
                >
                  <Column
                    field="remarcadorId"
                    header="Remarcador ID"
                    sortable
                  />
                  <Column field="qrCode" header="QR" sortable />
                  <Column
                    field="medidorPadreId"
                    header="Medidor padre"
                    sortable
                  />
                  <Column field="tipoId" header="Tipo" sortable />
                </DataTable>
              </TabPanel>
            </TabView>
          </TabPanel>

          {/* TAB DETALLE */}
          <TabPanel header="Detalle / Boletas">
            <div
              style={{
                marginBottom: 12,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 12,
                flexWrap: "wrap",
              }}
            >
              <div>
                <strong>Resultado</strong>
                <div style={{ color: "#6c757d", fontSize: 13 }}>
                  {lastQuery ? `Periodo ${lastQuery.periodo}` : "Sin consulta"}
                  {" · "}
                  {detalleItems.length} registros
                </div>
              </div>

              {detalleItems.length > 0 && (
                <Button
                  label="Generar boletas"
                  icon="pi pi-send"
                  onClick={generarBoletas}
                  disabled={!detalleItems?.length || isLoading}
                  className="p-button-success p-button-sm"
                />
              )}
            </div>

            <DataTable
              value={detalleItems}
              dataKey={(row) =>
                `${row?.codigoDomicilio ?? "NA"}|${row?.tipoServicio ?? "NA"}|${
                  row?.remarcadorId ?? "NA"
                }`
              }
              {...tablaProps}
            >
              <Column field="domicilio" header="Domicilio" sortable />
              <Column field="direccion" header="Dirección medidor" sortable />
              <Column
                header="Cliente"
                body={(row) => row?.cliente?.nombreRazonSocial ?? "-"}
                sortable
              />
              <Column header="RUT" body={(row) => row?.cliente?.rut ?? "-"} />
              <Column field="tipoServicio" header="Servicio" sortable />
              <Column field="unidad" header="Unidad" />
              <Column field="lecturaMesAnterior" header="Lect. ant." />
              <Column field="lecturaMesActual" header="Lect. act." />
              <Column field="consumoMesActual" header="Consumo" sortable />
              <Column
                header="Valor unit."
                body={(row) => `$ ${fmtMoneyCLP(row?.valorUnitario)}`}
                sortable
              />
              <Column
                header="Monto"
                body={(row) => `$ ${fmtMoneyCLP(row?.montoFinal)}`}
                sortable
              />
              <Column
                header="Boleta"
                body={(row) => {
                  const b = row?.boleta;
                  if (!b || !b.documentoId) return "—";
                  return (
                    <Button
                      label="Ver PDF"
                      icon="pi pi-file-pdf"
                      className="p-button-text p-button-sm"
                      onClick={() => abrirPdfFacturador(b)}
                    />
                  );
                }}
              />
            </DataTable>
          </TabPanel>
        </TabView>
      </div>
    </div>
  );
};

export default RevisionPeriodo;
